import React from 'react';
import { LocalServicePage } from '../../components/LocalServicePage';

export const LocksmithRomford = () => {
  return (
    <LocalServicePage
      areaName="Romford"
      areaType="City"
      slug="locksmith-romford"
      heroTitle="Door, Window & Lock Specialists in Romford"
      heroSubtitle="Royal Security is based in Romford, providing expert diagnosis and repairs for doors, windows, locks, and glazing across the local area. We focus on getting the repair right first time, not selling you replacements you don't need."
      introContent={
        <>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            As a Romford-based specialist, Royal Security understands the properties in this area inside out. From the Victorian terraces around Gidea Park to the post-war semis across Collier Row and the newer developments near the station, we've worked on every type of door and window system you'll find locally.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our approach is simple: diagnose properly, explain clearly, and repair thoroughly. Most door and window problems can be fixed without expensive replacements. A sticking uPVC door usually needs realignment and new hardware, not a whole new frame. A draughty window often just needs new seals and adjusted hinges. We'll always give you the honest assessment.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            Being based in Romford means we can respond quickly to urgent issues and return easily for any follow-up work. Many of our customers are neighbours, landlords with local portfolios, and letting agents managing properties across Havering—people we see regularly and whose trust we've built over time.
          </p>
        </>
      }
      servicesEmphasis={[
        {
          title: "Multipoint Lock Repairs",
          description: "The multipoint locking system is the heart of modern uPVC and composite doors. When gearboxes fail, handles break, or locking points seize, we diagnose the exact fault and repair or replace only the components that need attention. Full mechanism replacements when needed, sourced from quality suppliers."
        },
        {
          title: "Door Realignment & Adjustment",
          description: "Doors that drag, stick, or won't close properly are often misaligned rather than broken. We adjust hinges, reposition strike plates, and ensure your door closes smoothly and locks securely without forcing. This is usually far more effective than the 'new door' quote you might get elsewhere."
        },
        {
          title: "Tilt & Turn Window Repairs",
          description: "Tilt and turn windows have complex mechanisms that can fail in multiple ways—stuck in one position, won't lock, tilting when they should turn. We repair and replace the specific hardware at fault, restoring full functionality without replacing the entire window."
        },
        {
          title: "uPVC & Aluminium Door Repairs",
          description: "Whether it's a front door, patio door, or French doors, we work on all uPVC and aluminium systems. Handles, hinges, locks, letterboxes, cat flaps, panels—if it's part of your door, we can repair or upgrade it."
        },
        {
          title: "Double Glazing & Sealed Unit Repairs",
          description: "Misted double glazing, cracked panes, and failed seals are common problems we resolve daily. We replace sealed units within existing frames, with options to upgrade to improved thermal or acoustic glass if you want better performance."
        },
        {
          title: "Window Handle & Hinge Replacement",
          description: "Broken handles and worn hinges are straightforward repairs that make a real difference to security and ease of use. We carry a wide range of replacement parts and can usually complete these repairs in a single visit."
        }
      ]}
      localContextTitle="How Door & Window Repairs Work in Romford"
      localContext={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Before we arrive:</strong> When you contact us, we'll ask about the symptoms—what's happening, when it started, and any changes you've noticed. Photos of the problem can be helpful for initial assessment. This helps us bring appropriate parts and give you a realistic idea of what's involved.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>On-site diagnosis:</strong> We inspect the door or window to identify the root cause. A door that won't lock could be a cylinder, gearbox, alignment, or handle issue—each requires different repair. We'll explain what we find before starting any work.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            <strong>Parts and follow-up:</strong> Common parts are often available immediately. Some mechanisms, sealed units, or specific hardware may need ordering—we'll tell you upfront if a second visit is required. We guarantee our work and return if there are any issues.
          </p>
        </>
      }
      commonProblems={[
        {
          title: "Door won't lock or unlock smoothly",
          description: "Usually a worn gearbox or misaligned lock mechanism. We identify whether adjustment or replacement is needed."
        },
        {
          title: "Draughts around doors and windows",
          description: "Typically caused by worn seals, dropped hinges, or frames that need adjustment. Often a quick fix."
        },
        {
          title: "Misted or foggy double glazing",
          description: "The seal has failed between panes. We replace the sealed unit within your existing frame."
        },
        {
          title: "Window stuck open or closed",
          description: "Common with tilt & turn and casement windows. Usually a mechanism or hinge issue we can repair."
        },
        {
          title: "Handle broken or loose",
          description: "A simple but important repair. We match your existing hardware style wherever possible."
        },
        {
          title: "Door dropping or dragging on floor",
          description: "Hinges may need adjustment or replacement. We realign the door to close properly again."
        }
      ]}
      whyChooseUs={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            We're based in Romford because it's our home. We're not a national call centre dispatching whoever's available—we're a local specialist who knows the area, understands the typical issues, and takes pride in doing the job properly.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our diagnosis-first approach means we'll tell you exactly what's wrong before we start any work. If your door needs adjustment rather than a mechanism replacement, that's what we'll recommend. If it genuinely needs replacing, we'll explain why and give you options.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We work with homeowners, landlords, letting agents, and property managers throughout Romford and the surrounding areas. Many of our customers come back to us year after year for ongoing maintenance and new issues—that repeat business is built on trust, honest pricing, and work that lasts.
          </p>
        </>
      }
      ctaText="Need a Door, Window or Lock Specialist in Romford?"
    />
  );
};
